import { Suspense } from 'react';

import {
	mapContentItemToArticle,
	TopStories as TopStoriesData,
} from 'services/freeform';

import { useViewport } from 'hooks/use-viewport';

import { useUserStore } from 'stores/use-user-store';

import CardFeatured from 'core/generic/components/cards/card-featured';
import CardFixture from 'core/generic/components/cards/card-fixture';
import CardNews from 'core/generic/components/cards/card-news';

import { ShowMoreSection } from '@home/components/shared/show-more-section/show-more-section';
import { mapFixtureToCardProps } from '@home/utils/map-fixture-to-card-props';
import { NEWS_IMAGE_FALLBACK } from '@home/utils/news-image-fallback';

import styles from './top-stories.module.scss';

type TopStoriesProps = {
	data: TopStoriesData;
};

type ArticleLink = {
	url: string;
	title: string;
};

const SeoArticleList = ({
	articles,
	label,
}: {
	articles: ArticleLink[];
	label: string;
}) => (
	<ul aria-label={label}>
		{articles.map((article, index) => (
			<li key={`${article.url}-${index}`}>
				<a href={article.url}>{article.title}</a>
			</li>
		))}
	</ul>
);

export const TopStories = ({ data }: TopStoriesProps) => {
	const { viewportSize } = useViewport();
	const isMobile = viewportSize === 'mobile';
	const isUserLoggedIn = useUserStore((state) =>
		Boolean(state.user?.isLoggedIn)
	);

	const lead = mapContentItemToArticle(data.topStory);
	const others = (data.otherStories ?? []).map(mapContentItemToArticle);

	// Figma tablet/desktop: 2 medium + 8 small (two 4-col rows).
	const topCount = 2;
	const bottomLimit = 8;
	const topCards = others.slice(0, topCount);
	const bottomCards = others.slice(topCount, topCount + bottomLimit);
	const lastResult = data.lastResult ?? data.lastFixture;
	const hasAside = Boolean(data.nextFixture || lastResult);
	const allArticles = [lead, ...others];

	return (
		<section
			className={`${styles.root} ${
				hasAside ? styles['root--withAside'] : ''
			}`}
			aria-label="Featured news"
		>
			<div className={`container ${styles.container}`}>
				<header className={styles.header}>
					<Suspense fallback={<a href={lead.url}>{lead.title}</a>}>
						<CardFeatured
							url={lead.url}
							title={lead.title}
							caption={lead.primaryTag}
							image={{
								url: lead.image.url,
								alt: lead.image.alt,
							}}
							showVideoButton={lead.isVideo}
							signInRequired={Boolean(lead.isSignInRequired)}
							premiumVideo={Boolean(lead.isPremium)}
							isUserLoggedIn={isUserLoggedIn}
						/>
					</Suspense>
				</header>

				<div
					className={`${styles.layout} ${
						hasAside ? styles['layout--withAside'] : ''
					}`}
				>
					<div className={styles.content}>
						{isMobile ? (
							<ul className={styles.mobileList}>
								{others.map((article, index) => (
									<li
										key={`${article.url}-${index}`}
										className={styles.mobileListItem}
									>
										<Suspense
											fallback={
												<a href={article.url}>
													{article.title}
												</a>
											}
										>
											<CardNews
												fallback={NEWS_IMAGE_FALLBACK}
												data={article}
												size="Medium"
												type="Icon"
												direction="Row"
												fullWidth
												showVideoDuration={false}
											/>
										</Suspense>
										{index < others.length - 1 ? (
											<span
												className={styles.mobileDivider}
												aria-hidden="true"
											/>
										) : null}
									</li>
								))}
							</ul>
						) : (
							<>
								{topCards.length > 0 ? (
									<ul className={styles.topList}>
										{topCards.map((article, index) => (
											<li
												key={`${article.url}-${index}`}
												className={styles.listItem}
											>
												<Suspense
													fallback={
														<a href={article.url}>
															{article.title}
														</a>
													}
												>
													<CardNews
														fallback={
															NEWS_IMAGE_FALLBACK
														}
														data={article}
														size="Medium"
														type="Icon"
														direction="Column"
														fullWidth
														showVideoDuration={
															false
														}
													/>
												</Suspense>
											</li>
										))}
									</ul>
								) : null}

								{bottomCards.length > 0 ? (
									<ul className={styles.bottomList}>
										{bottomCards.map((article, index) => (
											<li
												key={`${article.url}-${index}`}
												className={styles.listItem}
											>
												<Suspense
													fallback={
														<a href={article.url}>
															{article.title}
														</a>
													}
												>
													<CardNews
														fallback={
															NEWS_IMAGE_FALLBACK
														}
														data={article}
														size="Medium"
														type="Icon"
														direction="Column"
														fullWidth
														showVideoDuration={
															false
														}
													/>
												</Suspense>
											</li>
										))}
									</ul>
								) : null}
							</>
						)}

						{/* Crawlable title list always present for bots that skip JS */}
						<noscript>
							<SeoArticleList
								articles={allArticles}
								label="All featured stories"
							/>
						</noscript>
					</div>

					{hasAside ? (
						<aside
							className={styles.aside}
							aria-label="Fixtures and results"
						>
							<div className={styles.asideCards}>
								{data.nextFixture ? (
									<Suspense fallback={null}>
										<CardFixture
											{...mapFixtureToCardProps(
												data.nextFixture
											)}
										/>
									</Suspense>
								) : null}

								{lastResult ? (
									<Suspense fallback={null}>
										<CardFixture
											{...mapFixtureToCardProps(
												lastResult
											)}
											variant="result"
										/>
									</Suspense>
								) : null}
							</div>
						</aside>
					) : null}

					{data.moreLink ? (
						<div className={styles['show-more']}>
							<ShowMoreSection
								link={data.moreLink}
								label={data.moreLabel || 'More news'}
							/>
						</div>
					) : null}
				</div>
			</div>
		</section>
	);
};

export default TopStories;
